// Phase MHM-1 · Main Hub Dashboard — 9 KPIs + 4 charts + 4 live widgets.
// MHM-5.1 — KPI cards are now clickable buttons that deep-link to filtered
// downstream pages (orders / agents / finance / analytics).
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package, CheckCircle2, AlertTriangle, Clock, Users, IndianRupee,
  Banknote, XCircle, Timer, RefreshCw, Truck, Building2
} from "lucide-react";
import { Loader2 } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Legend
} from "recharts";
import api from "../../services/api";
import { toast } from "sonner";
import MHMSubNav from "../../components/hub/MHMSubNav";
import TrackingIdLink from "../../components/hub/TrackingIdLink";
import RoleGate from "../../components/RoleGate";

const KPI_DEFS = [
  { key: "orders_today",         label: "Orders Today",        Icon: Package,       color: "text-violet-600",
    dest: "/dashboard/hub/orders?filter=today",
    aria: (v) => `Orders today, ${v ?? 0}. Click to view all orders created today.` },
  { key: "delivered_today",      label: "Delivered Today",     Icon: CheckCircle2,  color: "text-green-600",
    dest: "/dashboard/hub/orders?filter=delivered&date=today",
    aria: (v) => `Delivered today, ${v ?? 0}. Click to view orders delivered today.` },
  { key: "delayed_shipments",    label: "Delayed Shipments",   Icon: AlertTriangle, color: "text-amber-600",
    dest: "/dashboard/hub/orders?filter=delayed",
    aria: (v) => `Delayed shipments, ${v ?? 0}. Click to view orders past SLA.` },
  { key: "pending_pickups",      label: "Pending Pickups",     Icon: Clock,         color: "text-orange-600",
    dest: "/dashboard/hub/orders?filter=pickup_pending",
    aria: (v) => `Pending pickups, ${v ?? 0}. Click to view orders waiting for agent pickup.` },
  { key: "active_agents",        label: "Active Agents",       Icon: Users,         color: "text-blue-600",
    dest: "/dashboard/hub/agents?filter=active",
    aria: (v) => `Active agents, ${v ?? 0}. Click to view the active agents list.` },
  { key: "revenue_today",        label: "Revenue Today (₹)",   Icon: IndianRupee,   color: "text-green-600", money: true,
    dest: "/dashboard/hub/finance?tab=Summary&date=today",
    aria: (v) => `Revenue today, ${v ?? 0} rupees. Click to view today's finance summary.` },
  { key: "cod_pending",          label: "COD Pending (₹)",     Icon: Banknote,      color: "text-amber-600", money: true,
    dest: "/dashboard/hub/finance?tab=COD&status=pending",
    aria: (v) => `COD pending, ${v ?? 0} rupees. Click to view pending COD reconciliation.` },
  { key: "failed_deliveries",    label: "Failed Deliveries",   Icon: XCircle,       color: "text-rose-600",
    dest: "/dashboard/hub/orders?filter=failed",
    aria: (v) => `Failed deliveries, ${v ?? 0}. Click to view failed orders.` },
  { key: "avg_delivery_minutes", label: "Avg Delivery (min)",  Icon: Timer,         color: "text-slate-700",
    dest: "/dashboard/hub/analytics",
    aria: (v) => `Average delivery minutes, ${v ?? 0}. Click to view analytics.` },
];

function formatVal(k, v) {
  if (v == null) return "—";
  if (typeof v !== "number") return String(v);
  const def = KPI_DEFS.find((d) => d.key === k);
  if (def?.money) return v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  return v;
}

function KPICard({ k, value }) {
  const def = KPI_DEFS.find((d) => d.key === k);
  const navigate = useNavigate();
  if (!def) return null;
  const { label, Icon, color, dest, aria } = def;
  const onActivate = () => { if (dest) navigate(dest); };
  return (
    <button
      type="button"
      data-testid={`mhm-kpi-${k}`}
      onClick={onActivate}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onActivate(); }
      }}
      aria-label={aria ? aria(formatVal(k, value)) : `${label}: ${formatVal(k, value)}`}
      className="text-left w-full bg-white border border-slate-200 rounded-2xl p-4 shadow-sm
                 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0
                 transition-all duration-150 cursor-pointer
                 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00] focus-visible:ring-offset-2"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div className="text-2xl font-bold text-slate-900 font-['Outfit']">
        {formatVal(k, value)}
      </div>
    </button>
  );
}

function Card({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid={testid}>
      <div className="mb-3">
        <h3 className="text-base font-semibold text-slate-900 font-['Outfit']">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export default function MainHubDashboard() {
  const [kpis, setKpis] = useState(null);
  const [charts, setCharts] = useState(null);
  const [widgets, setWidgets] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, c, w, sla] = await Promise.all([
        api.get("/hub/dashboard/kpis"),
        api.get("/hub/dashboard/charts"),
        api.get("/hub/dashboard/live-widgets"),
        api.get("/hub/sla/alerts?status=active&limit=5").catch(() => ({ data: { rows: [], counts: {} } })),
      ]);
      setKpis(k.data); setCharts(c.data);
      // Merge real SLA data into the live widgets payload.
      const widgets = w.data;
      widgets.sla_alerts = {
        count: sla.data.counts?.active || 0,
        items: (sla.data.rows || []).slice(0, 5),
      };
      setWidgets(widgets);
    } catch (e) {
      toast.error(e.userMessage || "Couldn't load dashboard");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="mhm-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-dashboard-title">
              Main Hub Dashboard
            </h1>
            <p className="text-sm text-slate-500">
              {kpis?.hub_id ? `Scope: ${kpis.hub_id}` : "Loading scope…"} · auto-refreshes every 15s
            </p>
          </div>
          <button
            onClick={load}
            data-testid="mhm-dashboard-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>

        {/* 9 KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3">
          {KPI_DEFS.map((d) => (
            <KPICard key={d.key} k={d.key} value={kpis?.[d.key]} />
          ))}
        </div>

        {/* 4 Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Orders — last 24h" subtitle="Hourly bucket" testid="mhm-chart-hourly">
            <div className="w-full h-[220px]">
              <ResponsiveContainer>
                <LineChart data={charts?.hourly_orders || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="hour" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="orders" stroke="#7c3aed" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Delivery performance — 7 days" subtitle="Delivered vs failed vs delayed" testid="mhm-chart-performance">
            <div className="w-full h-[220px]">
              <ResponsiveContainer>
                <BarChart data={charts?.delivery_performance_7d || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="delivered" stackId="a" fill="#22c55e" />
                  <Bar dataKey="failed" stackId="a" fill="#ef4444" />
                  <Bar dataKey="delayed" stackId="a" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Revenue trend — 30 days" subtitle="Created-at sum of final_price" testid="mhm-chart-revenue">
            <div className="w-full h-[220px]">
              <ResponsiveContainer>
                <LineChart data={charts?.revenue_30d || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} hide />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="revenue" stroke="#16a34a" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Hub load — last 7 days" subtitle="Top micro hubs by volume" testid="mhm-chart-microload">
            <div className="w-full h-[220px]">
              <ResponsiveContainer>
                <BarChart data={charts?.micro_hub_load_7d || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={150} />
                  <Tooltip />
                  <Bar dataKey="orders" fill="#6366f1" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        {/* 4 Live widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card title="Real-time incoming parcels" subtitle="Last 10 entering the hub" testid="mhm-live-incoming">
            <div className="max-h-72 overflow-auto divide-y divide-slate-100">
              {(widgets?.incoming || []).length === 0 && (
                <div className="text-sm text-slate-400 py-6 text-center">No recent orders</div>
              )}
              {(widgets?.incoming || []).map((o) => (
                <div key={o.tracking_id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-mono text-xs"><TrackingIdLink tid={o.tracking_id} /></div>
                    <div className="text-xs text-slate-500">
                      {o.pickup_city || "—"} → {o.drop_city || "—"}
                    </div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{o.status}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Dispatch queue" subtitle="Manifests pending dispatch (MHM-2)" testid="mhm-live-dispatch">
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Truck className="h-4 w-4 text-slate-400" />
              <span data-testid="mhm-live-dispatch-count">
                {widgets?.dispatch_queue?.count ?? 0} orders queued
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {widgets?.dispatch_queue?.placeholder || "Dispatch Center ships in MHM-2."}
            </p>
          </Card>
          <Card title="SLA alerts" subtitle="Active red/yellow alerts" testid="mhm-live-sla">
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <span data-testid="mhm-live-sla-count">
                {widgets?.sla_alerts?.count ?? 0} active alerts
              </span>
            </div>
            <div className="mt-2 space-y-1 max-h-32 overflow-auto">
              {(widgets?.sla_alerts?.items || []).slice(0, 5).map((a) => (
                <div key={a.alert_id} className="text-xs text-slate-600 flex items-center gap-2">
                  <span className={`inline-block h-1.5 w-1.5 rounded-full ${
                    a.severity === "red" ? "bg-rose-500" : "bg-amber-500"
                  }`}></span>
                  <span className="truncate">{a.message}</span>
                </div>
              ))}
            </div>
          </Card>
          <Card title="Stuck shipments" subtitle="Same status > 6h (top 5)" testid="mhm-live-stuck">
            <div className="divide-y divide-slate-100 max-h-72 overflow-auto">
              {(widgets?.stuck_shipments || []).length === 0 && (
                <div className="text-sm text-green-600 py-6 text-center">All shipments on track</div>
              )}
              {(widgets?.stuck_shipments || []).map((o) => (
                <div key={o.tracking_id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-mono text-xs"><TrackingIdLink tid={o.tracking_id} /></div>
                    <div className="text-xs text-slate-500">{o.status} · {o.stuck_hours}h</div>
                  </div>
                  <Building2 className="h-4 w-4 text-amber-500" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </RoleGate>
  );
}
